import Vue from 'vue';
import Api from 'services/Api';
import store from 'store/index';
import moment from 'moment';

export const types = {
    REGISTER_MODAL: 'REGISTER_MODAL',
    UNREGISTER_MODAL: 'UNREGISTER_MODAL',
    TOGGLE_MODAL: 'TOGGLE_MODAL',
    UPDATE_MODAL_CONTENT: 'UPDATE_MODAL_CONTENT',
    RESET_MODAL: 'RESET_MODAL',
    CLOSE_MODAL: 'CLOSE_MODAL'
};

// Initial State
const state = {
    // List of modals, in the format name:isOpen (boolean)
    modals: {},
    content: [],
};

/**
 * Convert DD/MM/YYY to Server.
 *
 * This is only used by legacy payloads and endpoints, as the server now has
 * hydrators for this.
 *
 * @param  {String} date
 *
 * @return {String}
 */
export const dateToServer = date => date ? moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD') : null;

/**
 * Current Claim Type from Store.
 */
export const claimType = () => store.getters['claim/type'];

/**
 * Return the Callback for the Modal Endpoint
 *
 * @param  {String} calcType
 * @param {Object} [args] optional arguments for day one modals.
 *
 * @return {callback}
 */
const modalEndpoint = (calcType, args) => {
    switch (calcType) {
        /**
         * Claim Calculation Modals
         */
        case 'ogden_table':
            return Api.calculatorModals.lifeExpectancy.ogden;
        case 'percentage_of_normal_life_expectancy':
            return Api.calculatorModals.lifeExpectancy.percentageOfNormal;
        case 'percentage_reduction_in_normal_life_expectancy':
            return Api.calculatorModals.lifeExpectancy.percentageReduction;
        case 'table28':
            return Api.calculatorModals.multipliers.table28;
        case 'table1and2':
            return Api.calculatorModals.multipliers.table1And2;
        case 'pre':
            return Api.calculatorModals.multipliers.preAssessment;
        case 'post':
            return Api.calculatorModals.multipliers.postAssessment;
        case 'autoCare':
            return Api.calculatorModals.care.autoCalculate;
        case 'detailedCare':
            return Api.calculatorModals.care.detailed;
        case 'bereavementAward':
            return Api.calculatorModals.headsOfLoss.bereavementAward;
        case 'interest':
            return Api.calculatorModals.interest.default;
        case 'customInterest':
            return Api.calculatorModals.interest.custom;
        case 'educationAttainment':
            return Api.calculatorModals.education.educationAttainment;
        case 'expectedRetirementAge':
            return Api.calculatorModals.age.retirementAge;
        case 'contingencyMultiplier':
            return Api.calculatorModals.multipliers.contingencyMultiplier;
        case 'preInjuryContingencyMultiplierPayload':
            return Api.calculatorModals.multipliers.preInjuryContingencyMultiplierPayload;
        case 'assessmentContingencyMultiplierPayload':
            return Api.calculatorModals.multipliers.assessmentContingencyMultiplierPayload;
        case 'purchase':
            return Api.calculatorModals.multipliers.purchase;
        case 'postAssessmentMultiplier':
            return Api.calculatorModals.multipliers.postAssessmentMultiplier;
        case 'futureLossesForCare' :
            return Api.calculatorModals.care.futureLossesForCare;
        case 'futureLossesForServices' :
            return Api.calculatorModals.services.futureLossesForServices;
        case 'pensionLossMultiplier' :
            return Api.calculatorModals.pension.pensionLossMultiplier;
        case 'lumpSumMultiplier' :
            return Api.calculatorModals.pension.lumpSumMultiplier;

        /**
         * Day One Calculation Modals
         */
        case 'dayOneTable28Multiplier':
            return Api.calculatorModals.dayOne.table28Multiplier;
        case 'dayOneTableEDiscount':
            return Api.calculatorModals.dayOne.tableEDiscount;
        case 'dayOneTableFDiscount':
            return Api.calculatorModals.dayOne.tableFDiscount;
        case 'dayOneGeneralDamages':
            return Api.calculatorModals.dayOne.generalDamages;
        case 'dayOneBereavementAward':
            return Api.calculatorModals.dayOne.bereavementAward;
        case 'dayOneFuneralExpenses':
            return Api.calculatorModals.dayOne.funeralExpenses;
        case 'dayOnePastLossOfEarnings':
            return Api.calculatorModals.dayOne.pastLossOfEarnings;
        case 'dayOnePastCostOfServices':
            return Api.calculatorModals.dayOne.pastCostOfServices;
        case 'dayOneFutureCostOfServices':
            return Api.calculatorModals.dayOne.futureCostOfServices;
        case 'dayOneMiscellaneousCalc':
            return Api.calculatorModals.dayOne.miscellaneousCalc;
        case 'dayOneCostOfCare':
            return Api.calculatorModals.dayOne.costOfCare;
        case 'dayOneDependencyOnIncome':
            return Api.calculatorModals.dayOne.dependencyOnIncome;
        case 'dayOneDependencyOnServices':
            return Api.calculatorModals.dayOne.dependencyOnServices;
    }
}

/**
 * Return the Payload for the Modal Endpoint.
 *
 * Gradually refactor to return an unmolested payload here, so we can remove
 * the need for this method.
 *
 * @param  {String} calcType
 * @param  {Array} args
 *
 * @return {Object}
 */
const modalPayload = (calcType, payload) => {
    switch (calcType) {
        case 'ogden_table':
            return ogdenPayload(payload);
        case 'percentage_of_normal_life_expectancy':
            return percentagePayload(payload);
        case 'percentage_reduction_in_normal_life_expectancy':
            return percentagePayload(payload);
        case 'table28':
            return table28Payload(payload);
        case 'table1and2':
            return table1And2Payload(payload);
        case 'pre':
        case 'post':
            return prePostPayload(payload);
        case 'autoCare':
            return autoCarePayload();
        case 'detailedCare':
            return detailedCarePayload(payload);
        case 'bereavementAward':
            return bereavementAwardPayload(payload);
        case 'interest':
            return interestPayload(payload);
        case 'customInterest':
            return payload;
        case 'educationAttainment':
            return educationAttainmentPayload();
        case 'expectedRetirementAge':
            return retirementAgePayload(payload);
        case 'contingencyMultiplier':
            return contingencyMultiplierPayload(payload);
        case 'postAssessmentMultiplier':
            return postAssessmentMultiplier(payload);
        case 'preInjuryContingencyMultiplierPayload':
            return preInjuryContingencyMultiplierPayload(payload);
        case 'assessmentContingencyMultiplierPayload':
            return assessmentContingencyMultiplierPayload(payload);
        case 'purchase':
            return purchasePayload(payload);
        case 'futureLossesForCare' :
            return futureLossesForCare(payload);
        case 'futureLossesForServices' :
            return futureLossesForServices(payload);
        case 'pensionLossMultiplier' :
            return pensionLossMultiplier(payload);
        case 'lumpSumMultiplier' :
            return lumpSumMultiplier(payload);
        default:
            return payload.args;
    }
};

let autoCarePayload = () => {
    return {
        claim: {
            type: claimType(),
        },
    }
};

let ogdenPayload = ({assumedSettlement, person, type}) => {
    return {
        type,
        person: {
            name: person.name,
            gender: person.gender,
            date_of_birth: dateToServer(person.dateOfBirth),
        },
        claim: {
            assumed_settlement: dateToServer(assumedSettlement),
            type: claimType(),
        },
    };
};

let percentagePayload = ({assumedSettlement, person, type, value}) => {
    return {
        type,
        person: {
            name: person.name,
            gender: person.gender,
            date_of_birth: dateToServer(person.dateOfBirth),
        },
        claim: {
            assumed_settlement: dateToServer(assumedSettlement),
            type: claimType(),
        },
        value,
    };
};

let table28Payload = ({assumedSettlement, person, discountRate, duration}) => {
    return {
        person: {
            name: person.name,
            gender: person.gender,
            date_of_birth: dateToServer(person.dateOfBirth),
            date_of_death_assumed: dateToServer(person.lifeExpectancy.dateOfDeath),
        },
        claim: {
            assumed_settlement: dateToServer(assumedSettlement),
            type: claimType(),
        },
        discount_rate: discountRate,
        duration: duration,
    };
};

let table1And2Payload = ({assumedSettlement, person, discountRate, duration}) => {
    return {
        person: {
            name: person.name,
            gender: person.gender,
            date_of_birth: dateToServer(person.dateOfBirth),
            date_of_death_assumed: dateToServer(person.lifeExpectancy.dateOfDeath),
        },
        claim: {
            assumed_settlement: dateToServer(assumedSettlement),
            type: claimType(),
        },
        discount_rate: discountRate,
        duration: duration,
    };
};

let prePostPayload = ({assumedSettlement, person}) => {
    return {
        person: {
            name: person.name,
            gender: person.gender,
            date_of_birth: dateToServer(person.dateOfBirth),
            date_of_death: dateToServer(person.dateOfDeath),
        },
        claim: {
            assumed_settlement: dateToServer(assumedSettlement),
            type: claimType(),
        },
    };
};

let postAssessmentMultiplier = ({period, discountRate}) => {
    return {
        period: {
            period_start: period.period_from,
            period_end: period.period_to,
            period_duration: period.duration_years,
            period_deferment: period.deferment,
            period_income_multiplicand: period.income_multiplicand,
            period_discounted_multiplicand: period.discounted_multiplicand,
            period_multiplier: period.multiplier,
            period_value: period.value,
        },
        claim: {
            type: claimType(),
        },
        discount_rate: discountRate,
    };
};

let detailedCarePayload = ({period}) => {
    return {
        care_period: {
            period_start: period.periodStart,
            period_end: period.periodEnd,
            hours_per_day: period.hoursPerDay,
            number_of_days: period.numberOfDays,
            rate: period.rate,
            total: period.total,
            gratuitous_care: period.gratuitousCare,
            include_first_day: period.includeFirstDay,
            include_last_day: period.includeLastDay
        },
        claim: {
            type: claimType(),
        },
    };
};

let futureLossesForCare = ({period, discountRate, person}) => {
    return {
        period,
        person: {
            gender: person.gender,
            date_of_birth: dateToServer(person.date_of_birth),
        },
        claim: {
            type: claimType(),
        },
        claimInfo: {
            discount_rate: discountRate,
            date_of_assumed_settlement: dateToServer(person.date_of_assumed_settlement),
        }
    };
};

let futureLossesForServices = ({service, discountRate, person}) => {
    return {
        service,
        person: {
            gender: person.gender,
            date_of_birth: dateToServer(person.date_of_birth),
        },
        claim: {
            type: claimType(),
        },
        claimInfo: {
            discount_rate: discountRate,
            date_of_assumed_settlement: dateToServer(person.date_of_assumed_settlement),
        }
    };
};

let pensionLossMultiplier = ({pension, claimant, claimInfo}) => {
    return {
        pension,
        claimInfo: {
            discount_rate: claimInfo.discountRate,
            date_of_assumed_settlement: dateToServer(claimInfo.dateOfAssumedSettlement),
        },
        person: {
            gender: claimant.gender,
            date_of_birth: dateToServer(claimant.dateOfBirth),
        },
        claim: {
            type: "personal_injury",
        },
    };
};

let lumpSumMultiplier = ({pension, claimant, claimInfo}) => {
    return {
        pension,
        claimInfo: {
            discount_rate: claimInfo.discountRate,
            date_of_assumed_settlement: dateToServer(claimInfo.dateOfAssumedSettlement),
        },
        person: {
            gender: claimant.gender,
            date_of_birth: dateToServer(claimant.dateOfBirth),
        },
        claim: {
            type: "personal_injury",
        },
    };
};

let bereavementAwardPayload = ({person, type = claimType()}) => {
    return {
        person: {
            name: person.name,
            gender: person.gender,
            date_of_birth: dateToServer(person.dateOfBirth),
            date_of_death: dateToServer(person.dateOfDeath),
        },
        claim: {type},
    };
};

let interestPayload = ({
                           interimPayments,
                           assumedSettlement,
                           serviceOfProceedings,
                           onsetOfSymptoms,
                           assumedLifeExpectancy,
                           deductibles,
                           headsOfLoss,
                           headOfLoss
                       }) => {
    return {
        claim: {
            assumed_settlement: dateToServer(assumedSettlement),
            service_of_proceedings: dateToServer(serviceOfProceedings),
            type: claimType()
        },
        deceased: {
            onset_of_symptoms: dateToServer(onsetOfSymptoms),
        },
        deductibles,
        heads_of_loss: {
            // Don't apply general damages to preLit claims
            pastDependencyOnIncome: headsOfLoss.pastDependencyOnIncome,
            pastDependencyOnServices: headsOfLoss.pastDependencyOnServices || headsOfLoss.pastCostOfServices,
            generalDamages: headsOfLoss.generalDamages,
            bereavementAward: headsOfLoss.bereavementAward,
            funeralExpenses: headsOfLoss.funeralExpenses,
            lossOfSpouse: headsOfLoss.lossOfSpouse,
            pastLossOfEarnings: headsOfLoss.pastLossOfEarnings,
            pastCostOfCare: headsOfLoss.pastCostOfCare,
            futureCostOfCare: headsOfLoss.futureCostOfCare,
            pastCostOfTravel: headsOfLoss.pastCostOfTravel,
            misc: headsOfLoss.misc,
        },
        head_of_loss: headOfLoss,
        interest: {
            pastDependencyOnIncomeInterest: headsOfLoss.pastDependencyOnIncomeInterest,
            pastDependencyOnServicesInterest: headsOfLoss.pastDependencyOnServicesInterest || headsOfLoss.pastCostOfServicesInterest,
            generalDamagesInterest: headsOfLoss.generalDamagesInterest,
            bereavementAwardInterest: headsOfLoss.bereavementAwardInterest,
            funeralExpensesInterest: headsOfLoss.funeralExpensesInterest,
            lossOfSpouseInterest: headsOfLoss.lossOfSpouseInterest,
            pastCostOfCareInterest: headsOfLoss.pastCostOfCareInterest,
            futureCostOfCareInterest: headsOfLoss.futureCostOfCareInterest,
            pastCostOfTravelInterest: headsOfLoss.pastCostOfTravelInterest,
            pastLossOfEarningsInterest: headsOfLoss.pastLossOfEarningsInterest,
            miscInterest: headsOfLoss.miscInterest,
        },
        interestPeriod: {
            type: headsOfLoss.interestType,
            value: headsOfLoss.customInterestDate,
        },
        interimPayments: interimPayments,
        custom_heads_of_loss: headsOfLoss.customHeadsOfLoss ? headsOfLoss.customHeadsOfLoss : headsOfLoss.headsOfLoss
    };
};

let customInterestPayload = ({assumedSettlement, person, customHeadOfLoss}) => {
    return {
        claim: {
            assumed_settlement: dateToServer(assumedSettlement),
            type: claimType(),
        },
        deceased: {
            date_of_death: dateToServer(person.dateOfDeath),
        },
        custom: customHeadOfLoss,
    };
};

let educationAttainmentPayload = () => {
    return {
        claim: {
            type: claimType(),
        },
    }
};

let retirementAgePayload = ({person}) => {
    return {
        claim: {
            type: claimType(),
        },
        person,
    }
};

let contingencyMultiplierPayload = ({assumedSettlement, person}) => {
    return {
        claim: {
            type: claimType(),
            disabled: person.disabledAtAccident,
            employedAtDeath: person.employedAtDateOfDeath ? 'employed' : 'notEmployed',
            levelOfEducation: person.levelOfEducation,
            assumedSettlement,
        },
        person,
        employedAtAccident: person.employedAtAccident,
    }
};

let preInjuryContingencyMultiplierPayload = ({disabled, education, employment, gender, dateOfBirth, assumedSettlement}) => {
    return {
        claim: {
            type: claimType(),
        },
        disabled,
        education,
        employment,
        gender,
        dateOfBirth,
        assumedSettlement
    }
};

let assessmentContingencyMultiplierPayload = ({disabled, education, employment, gender, dateOfBirth, assumedSettlement}) => {
    return {
        claim: {
            type: claimType(),
        },
        disabled,
        education,
        employment,
        gender,
        dateOfBirth,
        assumedSettlement
    }
};

let purchasePayload = ({purchase, claimInfo}) => {
    return {
        purchase: {
            description: purchase.description,
            discount: purchase.discount,
            frequency: purchase.frequency,
            multiplicand: purchase.multiplicand,
            multiplier: purchase.multiplier,
            purchase_date: purchase.purchaseDate,
            purchase_end_date: purchase.purchaseEnd,
            purchase_until: purchase.purchaseUntil,
            resale_option: purchase.resaleOption,
            resale_total: purchase.resaleTotal,
            resale_value: purchase.resaleValue,
            mortality_multiplier: purchase.mortalityMultiplier,
            total: purchase.total,
            type: purchase.type,
            assumed_settlement: claimInfo.date_of_assumed_settlement,
            date_of_birth: claimInfo.date_of_birth,
            discount_rate: claimInfo.discount_rate,
            gender: claimInfo.gender,
        },
        claim: {
            type: claimType(),
        },
    };
};

// Getters
const getters = {
    /**
     * Get all of the Modals.
     */
    modals: state => state.modals,
    /**
     * Get the current Modal Content.
     */
    modalContent: state => state.content,
};

// Actions
const actions = {
    /**
     * Register a Modal.
     */
    register: ({commit}, name) => commit(types.REGISTER_MODAL, name),
    /**
     * Toggle a Modal to Show or Hide.
     */
    toggle: ({commit}, name) => {
        commit(types.TOGGLE_MODAL, name)
    },
    /**
     * Unregister a Modal.
     */
    unregister: ({commit}, name) => commit(types.UNREGISTER_MODAL, name),
    /**
     * Performs an API request to get the Modal Content.
     */
    getModalContent({dispatch}, {endpoint, payload}) {
        // Find our matching Endpoint - this should be unncessary as our Vue
        // Component should know the type of request being performed based
        // on context (for instance Ogden Life Expectancy).
        let hitEndpoint = modalEndpoint(endpoint);

        // Determine/Adjust the Payload — again this should be unnecessary
        // as our Vue Component should be able to provide the arguments
        // provided through as a the expected payload... therefore we
        // may remove this at a later date.
        let withPayload = modalPayload(endpoint, payload);

        // Perform the request, grab the results and update the modal content.
        return hitEndpoint(withPayload).then(
            result => dispatch('updateModalContent', result)
        );
    },
    /**
     * Update a Modal's Content.
     */
    updateModalContent({commit}, content) {
        commit(types.UPDATE_MODAL_CONTENT, content);
    },
    /**
     * Reset the Modal Content
     */
    resetModal({commit}) {
        commit(types.RESET_MODAL);
    },

    closeModal: ({commit}, name) => commit(types.CLOSE_MODAL, name)
};

// Mutations
const mutations = {
    [types.REGISTER_MODAL](state, name) {
        Vue.set(state.modals, name, false);
    },
    [types.UNREGISTER_MODAL](state, name) {
        delete state.modals[name];
    },
    [types.TOGGLE_MODAL](state, name) {
        state.modals[name] = !state.modals[name];
    },
    [types.UPDATE_MODAL_CONTENT](state, content) {
        state.content = content;
    },
    [types.RESET_MODAL](state) {
        state.content = [];
    },
    [types.CLOSE_MODAL](state, name) {
        state.modals[name] = false;
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/modal.js